// The after-hours night watch on the Cameras pages (jobq-api "AFTER-HOURS SECURITY", 2026-10-15):
// ONE copy of the reads, the display rules and the two buttons, used by the desktop Cameras page
// (pages/CamerasPage.jsx + components/cameras/SecurityPill / AlertsView / PlaybackView / LiveView) and
// the phone Cameras screen (mobile/screens/CamerasScreen.jsx). Actions take a `ui`
// (features/desktopUi.js or mobile/ui/phoneUi.js).
//
// Every call goes through securityFetch (the admin session's X-Admin-Pin). The two buttons ALSO send the
// PIN typed for that press (ui.pin(..., {autofill: false})): it decides whose name goes on it, and a shop
// PC left in an admin session must not switch the watch off with one click (Mike, 2026-10-13).
//
// Times from the API are ISO; everything shown is shop (Central) time.
import { centralMinute, shiftDate, todayCentral } from '../../utils/cameraRelay';
import { securityFetch } from '../../utils/securityApi';
import { doorAlertTitle, doorOutcomeLabel, isDoorAlert } from './doors';

// ── reads ────────────────────────────────────────────────────────────────────────────────────────

/** GET /security/status: the pill. Resolves securityFetch's {ok, status, body, message}. */
export const fetchNightWatchStatus = () => securityFetch('/security/status');

/** GET /security/alerts (default: the last 7 days). */
export function fetchNightWatchAlerts({ since, until, cameraKey, limit = 200 } = {}) {
  const q = new URLSearchParams();
  if (since) q.set('since', since);
  if (until) q.set('until', until);
  if (cameraKey) q.set('camera_key', cameraKey);
  q.set('limit', String(limit));
  return securityFetch(`/security/alerts?${q}`);
}

/** GET /security/timeline for [since, until) (at most 48 h). */
export function fetchNightWatchTimeline({ since, until }) {
  const q = new URLSearchParams({ since, until });
  return securityFetch(`/security/timeline?${q}`);
}

// ── Central time ─────────────────────────────────────────────────────────────────────────────────

const CLOCK = new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit' });

/** "3:22 PM" (Central) from an ISO time or a Date; '' for nothing. */
export function shopClock(t) {
  if (!t) return '';
  const d = t instanceof Date ? t : new Date(t);
  return Number.isNaN(d.getTime()) ? '' : CLOCK.format(d);
}

/** The instant of minute `minute` of Central day `ymd` (handles both UTC offsets). */
export function centralDate(ymd, minute) {
  const [y, m, d] = ymd.split('-').map(Number);
  const dayUtc = Date.UTC(y, m - 1, d);
  let t = dayUtc + minute * 60000 + 6 * 3600000; // a CST guess, then corrected
  for (let i = 0; i < 3; i += 1) {
    const at = new Date(t);
    const [cy, cm, cd] = todayCentral(at).split('-').map(Number);
    const err = ((Date.UTC(cy, cm - 1, cd) - dayUtc) / 86400000) * 1440 + centralMinute(at) - minute;
    if (Math.abs(err) < 1e-6) break;
    t -= err * 60000;
  }
  return new Date(t);
}

/** The night an alert belongs to: the Central date of (time − 6 h), so 2 AM is the evening before. */
export function nightOf(t) {
  const d = t instanceof Date ? t : new Date(t);
  return todayCentral(new Date(d.getTime() - 6 * 3600000));
}

/** "Tonight": 6 PM → 6 AM. In the daytime (6 AM – 6 PM) it is last night. */
export function tonightWindow(now = new Date()) {
  let night = nightOf(now);
  if (now.getTime() < centralDate(night, 18 * 60).getTime()) night = shiftDate(night, -1);
  return { night, start: centralDate(night, 18 * 60), end: centralDate(shiftDate(night, 1), 6 * 60) };
}

/** Minute of Central day `ymd` for an ISO time, clamped to [0, 1440] (spans crossing midnight). */
export function minuteOnDay(t, ymd) {
  const d = t instanceof Date ? t : new Date(t);
  const [y, m, dd] = ymd.split('-').map(Number);
  const [cy, cm, cd] = todayCentral(d).split('-').map(Number);
  const dayDiff = (Date.UTC(cy, cm - 1, cd) - Date.UTC(y, m - 1, dd)) / 86400000;
  return Math.max(0, Math.min(1440, dayDiff * 1440 + centralMinute(d)));
}

/** Where "Watch clip" opens: the alert's camera 30 s before it was seen. */
export function clipTarget(alert) {
  const t = new Date(new Date(alert.seen_at).getTime() - 30000);
  return { cameraKey: alert.camera_key || null, day: todayCentral(t), minute: centralMinute(t) };
}

const ago = (sec) => {
  if (sec == null) return '';
  if (sec < 90) return `${sec} s ago`;
  const m = Math.round(sec / 60);
  return m < 90 ? `${m} min ago` : `${Math.round(m / 60)} h ago`;
};

const firstName = (who) => String(who || '?').trim().split(/\s+/)[0];

/** ", extended by Mike" when someone else extended a snooze; ", extended" when its starter did; "" otherwise. */
export function extendedNote(z) {
  if (!z || !(z.extended_count > 0)) return '';
  return z.extended_by && z.extended_by !== z.by ? `, extended by ${firstName(z.extended_by)}` : ', extended';
}

// ── the pill ─────────────────────────────────────────────────────────────────────────────────────

export const SOURCE_LABEL = { phone: 'phone', manual: "I'm here", timeclock: 'TimeClock', jobq: 'JobQ punch' };
const SOURCE_PHRASE = { phone: 'checked in', manual: "pressed I'm here", timeclock: 'on the clock', jobq: 'in JobQ' };

/** "Brady on the clock, Jesse in JobQ" (at most two, then "+N"). */
export function whoIsHere(present) {
  const parts = (present || []).map((p) => `${firstName(p.who)} ${SOURCE_PHRASE[p.source] || 'here'}`);
  return parts.length > 2 ? `${parts.slice(0, 2).join(', ')} +${parts.length - 2}` : parts.join(', ');
}

/**
 * The pill's one line from GET /security/status: {state, tone, main, side}. tone: bad | warn | watch | muted.
 * Watcher offline wins at any hour (the Pi was stopped 10/14 02:30-09:18 and nothing showed it).
 */
/** The dev site: the camera Pi sends its heartbeats to PROD, so dev only ever has test beats. */
export const isDevSite = (status) => !!status?.environment && status.environment !== 'prod';

export function pillView(status, now = new Date()) {
  if (!status) return { state: 'loading', tone: 'muted', main: 'Night watch…', side: '' };
  const w = status.watcher || {};
  const armed = status.armed || {};
  if (status.state === 'offline' && isDevSite(status)) {
    return { state: 'dev', tone: 'muted', main: 'Night watch (dev)', side: 'the camera Pi reports to prod' };
  }
  switch (status.state) {
    case 'offline':
      return { state: 'offline', tone: 'bad', main: 'Watcher offline',
               side: w.last_beat_at ? `last heard ${ago(w.age_s)}` : 'not heard from yet' };
    case 'holding': {
      const h = status.hold || {};
      const left = Math.max(0, Math.round((new Date(h.due_at).getTime() - now.getTime()) / 1000));
      return { state: 'holding', tone: 'bad', main: 'Holding an alert',
               side: `${h.camera || 'a camera'}, texts in ${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}` };
    }
    case 'schedule_only':
      return { state: 'schedule_only', tone: 'warn', main: 'Schedule only', side: "the Pi can't reach JobQ" };
    case 'snoozed': {
      const z = status.snooze || {};
      return { state: 'snoozed', tone: 'warn', main: 'Snoozed', side: `${firstName(z.by)} · until ${shopClock(z.until)}` };
    }
    case 'armed': {
      const n = (w.cameras_watched || []).length;
      const side = [n ? `${n} camera${n === 1 ? '' : 's'}` : '', w.mode === 'shadow' ? 'test mode, no texts' : ''].filter(Boolean).join(' · ');
      return { state: 'armed', tone: 'watch', main: 'Armed', side };
    }
    case 'not_armed':
      return { state: 'not_armed', tone: 'warn', main: 'Not armed', side: whoIsHere(armed.present) };
    default: {
      const starts = status.watch_hours?.starts;
      const [hh, mm] = (starts || '18:00').split(':').map(Number);
      return { state: 'day', tone: 'muted', main: 'Day',
               side: `watch starts ${shopClock(centralDate(todayCentral(now), hh * 60 + mm))}` };
    }
  }
}

/** The panel's sentence under the title. */
export function pillWhy(status) {
  if (!status) return '';
  const w = status.watcher || {};
  if (status.state === 'offline' && isDevSite(status)) {
    return 'This is the dev site. The camera Pi sends its heartbeats to prod, so dev only has test ones: ' +
      'the real watch is on jobqworks.com. Who counts as here (below) is dev\'s copy.';
  }
  switch (status.state) {
    case 'offline':
      return w.why ? `${w.why[0].toUpperCase()}${w.why.slice(1)}: it is NOT watching, and won't arm tonight until it is back.`
        : 'The camera Pi is not watching.';
    case 'holding':
      return `${status.hold?.kind === 'vehicle' ? 'A vehicle' : 'A person'} on ${status.hold?.camera || 'a camera'}. ` +
        "The owners get a text when the 2-minute hold ends, unless someone presses I'm here or snoozes.";
    case 'schedule_only':
      return `The Pi is arming by the clock alone (${w.armed_error || 'JobQ did not answer'}): check-ins and snoozes don't reach it.`;
    case 'snoozed':
      return `${status.snooze?.by || 'Someone'} snoozed the whole shop${status.snooze?.reason ? ` ("${status.snooze.reason}")` : ''}` +
        `${extendedNote(status.snooze)}. It re-arms on its own at ${shopClock(status.snooze?.until)}.`;
    case 'armed':
      return 'Watching: nobody is known to be here.';
    case 'not_armed':
      return 'Watch hours, but someone counts as here, so nothing alerts.';
    default:
      return `Outside watch hours (${status.watch_hours?.label || 'weeknights 6 PM – 5:30 AM, weekends all day'}).`;
  }
}

// A clock-in this old during watch hours is worth a look (a forgotten clock-out keeps the shop disarmed
// until TimeClock's 14 h cutoff).
const LONG_CLOCK_IN_HOURS = 6;

const duration = (ms) => {
  const m = Math.max(0, Math.round(ms / 60000));
  return m < 60 ? `${m} m` : `${Math.floor(m / 60)} h ${m % 60} m`;
};

/** How long a signal keeps the watch off if nothing changes (`counts_until`: jobq-api's 14 h cap on a
 * check-in or clock-in, 30 min after a JobQ punch). */
function keepsOff(p) {
  const t = shopClock(p.counts_until);
  if (p.source === 'jobq') return `keeps the watch off until ${t} unless they punch again`;
  if (p.source === 'timeclock') return `keeps the watch off until they clock out (at the latest ${t})`;
  return `keeps the watch off until they leave (at the latest ${t})`;
}

/** One row per presence signal: {who, source, since, until, long}. */
export function presenceRows(status, now = new Date()) {
  const rows = [];
  for (const p of status?.armed?.present || []) {
    const since = p.since ? new Date(p.since) : null;
    const ms = since ? now.getTime() - since.getTime() : 0;
    const label = SOURCE_LABEL[p.source] || p.source;
    rows.push({ key: `${p.who}|${p.source}`, who: p.who,
                source: p.source === 'timeclock' && p.job ? `${label} · ${p.job}` : label,
                since: since ? `on since ${shopClock(since)} · ${duration(ms)}` : '',
                until: p.counts_until ? keepsOff(p) : '',
                long: p.source === 'timeclock' && ms >= LONG_CLOCK_IN_HOURS * 3600000 });
  }
  for (const p of status?.armed?.ignored || []) {
    const label = SOURCE_LABEL[p.source] || p.source;
    rows.push({ key: `${p.who}|${p.source}|ignored`, who: p.who, source: p.job ? `${label} · ${p.job}` : label,
                since: '', until: p.why || 'not counted', ignored: true });
  }
  return rows;
}

/** The panel's small print: sources, hours, the Pi. */
export function panelFoot(status) {
  if (!status) return [];
  const sig = status.armed?.signals || {};
  const bad = Object.entries(sig).filter(([, v]) => v !== 'ok');
  const w = status.watcher || {};
  const lines = [bad.length ? `Sources: ${bad.map(([k, v]) => `${k} ${v}`).join(' · ')}` : 'Sources: phones ok · TimeClock ok · JobQ ok'];
  if (status.watch_hours?.label) lines.push(`Hours: ${status.watch_hours.label}`);
  if (isDevSite(status) && !w.online) {
    lines.push(`Pi: reports to prod, so dev only has test heartbeats${w.last_beat_at ? ` (the last ${ago(w.age_s)})` : ''}`);
  } else if (w.last_beat_at) {
    const n = (w.cameras_watched || []).length;
    const off = (w.cameras_ignored || []).length;
    lines.push(`Pi: ${w.mode || '?'} mode · heard ${ago(w.age_s)} · watching ${n}${off ? ` (${off} never watched)` : ''}` +
      (w.snap_errors ? ` · ${w.snap_errors} frame errors in the last beat` : ''));
  } else {
    lines.push('Pi: no heartbeat yet');
  }
  return lines;
}

// ── tiles ────────────────────────────────────────────────────────────────────────────────────────

/** The tile's tag during watch hours: {kind: watched | ignored | snoozed, label} or null. */
export function tileWatch(status, cameraKey) {
  if (!status || !status.armed?.in_watch_window || !cameraKey) return null;
  const snooze = (status.camera_snoozes || []).find((z) => z.camera_key === cameraKey);
  if (snooze) return { kind: 'snoozed', label: `Snoozed until ${shopClock(snooze.until)}` };
  const w = status.watcher || {};
  if (isDevSite(status) && !w.online) return null;   // dev's camera lists are a test heartbeat's
  if ((w.cameras_ignored || []).includes(cameraKey)) return { kind: 'ignored', label: 'Not watched at night' };
  if ((w.cameras_watched || []).includes(cameraKey)) return { kind: 'watched', label: 'Watched' };
  return null;
}

const KIND_WORD = (a) => (a.kind === 'vehicle' ? `Vehicle${a.cls && a.cls !== 'vehicle' ? ` (${a.cls})` : ''}` : 'Person');

/** The caption that replaces the tile's activity line for 12 h after an alert: {text, tone} or null. */
export function tileAlert(status, cameraKey) {
  const a = (status?.recent_alerts || []).find((x) => x.camera_key === cameraKey);
  if (!a) return null;
  return { text: `${KIND_WORD(a)} ${shopClock(a.seen_at)} · ${outcomeLabel(a)}`, tone: OUTCOMES[a.outcome]?.tone || 'held',
           alert: a };
}

// ── alerts ───────────────────────────────────────────────────────────────────────────────────────

// not_texted = an alert that should have gone out and reached nobody (every send failed): first.
export const OUTCOMES = {
  not_texted: { rank: 0, tone: 'bad', filter: 'texted' },
  texted: { rank: 1, tone: 'bad', filter: 'texted' },
  unsure: { rank: 2, tone: 'warn', filter: 'texted' },
  held: { rank: 3, tone: 'held', filter: 'held' },
  let_go: { rank: 4, tone: 'letgo', filter: 'let_go' },
  dropped: { rank: 5, tone: 'dropped', filter: 'dropped' },
};

export const ALERT_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'texted', label: 'Texted' },
  { key: 'held', label: 'Held by Claude' },
  { key: 'let_go', label: 'Let go' },
  { key: 'dropped', label: 'Dropped' },
];

/** "Texted Mike, Jesse" / "Held by Claude · not texted" / "Dropped · Mike checked in" … */
export function outcomeLabel(a) {
  if (isDoorAlert(a)) return doorOutcomeLabel(a);   // a door alert (source 'door', 2026-10-25)
  switch (a.outcome) {
    case 'not_texted':
      return `NOT texted${a.errors?.length ? `: ${a.errors.join('; ')}` : ''}`;
    case 'texted':
      return a.texted?.length ? `texted ${a.texted.join(', ')}` : 'no text went out';
    case 'unsure':
      return a.texted?.length ? `texted ${a.texted.join(', ')} as unsure` : 'unsure, no text went out';
    case 'held':
      return 'held by Claude, not texted';
    case 'let_go':
      return `let go, probably ${a.note || 'an owner'} leaving`;
    case 'dropped':
      return `dropped: ${a.note || 'someone checked in'}`;
    default:
      return a.outcome || '';
  }
}

export function alertTitle(a) {
  if (isDoorAlert(a)) return doorAlertTitle(a);
  return `${KIND_WORD(a)} on ${a.camera} · ${shopClock(a.seen_at)}`;
}

/** Nights newest first, each alert ranked by stakes (texted > unsure > held > let go > dropped), then newest. */
export function groupAlertsByNight(alerts, filter = 'all', cameraKey = null) {
  const keep = (alerts || []).filter((a) => (filter === 'all' || OUTCOMES[a.outcome]?.filter === filter)
    && (!cameraKey || a.camera_key === cameraKey));
  const nights = new Map();
  for (const a of keep) {
    const n = nightOf(a.seen_at);
    if (!nights.has(n)) nights.set(n, []);
    nights.get(n).push(a);
  }
  return [...nights.entries()]
    .sort((x, y) => (x[0] < y[0] ? 1 : -1))
    .map(([night, list]) => ({
      night,
      alerts: list.sort((x, y) => ((OUTCOMES[x.outcome]?.rank ?? 9) - (OUTCOMES[y.outcome]?.rank ?? 9))
        || (x.seen_at < y.seen_at ? 1 : -1)),
    }));
}

/** The phone's recent list: camera and door alerts of the last `hours`, newest first. */
export function recentAlerts(alerts, now = new Date(), hours = 24) {
  const from = now.getTime() - hours * 3600000;
  return (alerts || [])
    .filter((a) => { const t = new Date(a.seen_at).getTime(); return Number.isFinite(t) && t >= from; })
    .sort((x, y) => (x.seen_at < y.seen_at ? 1 : -1));
}

/** A stable list key for a camera alert (incident_id) or a door alert (event_id). */
export const alertKey = (a) => (a.source === 'door' ? `door-${a.event_id}` : `cam-${a.incident_id}`);

/** "since" for the phone's last-24-h read: 24 h back, on the hour (stable between polls). */
export const recentSince = (now = new Date(), hours = 24) =>
  new Date(Math.floor((now.getTime() - hours * 3600000) / 3600000) * 3600000).toISOString();

// ── the Tonight strip and the day bar's lane ─────────────────────────────────────────────────────

const pct = (t, w) => Math.max(0, Math.min(100, ((t - w.start.getTime()) / (w.end.getTime() - w.start.getTime())) * 100));

/**
 * The strip's lanes over window {start, end} (Dates): [{key, label, blocks: [{left, width, kind, title}],
 * ticks: [{left, kind, title}]}], percentages of the window. Kinds: armed | off | offline | fallback |
 * snoozed | present | ghost (still counting if nothing changes) | <alert outcome>.
 */
export function stripLanes(timeline, status, w, now = new Date()) {
  const lanes = [];
  const span = (a, b, kind, title, label = '') => {
    const left = pct(new Date(a).getTime(), w);
    const right = pct(new Date(b).getTime(), w);
    return right > left ? { left, width: right - left, kind, title, label } : null;
  };
  const watchKind = { armed: 'armed', disarmed: 'off', offline: 'offline', fallback: 'fallback', snoozed: 'snoozed' };
  const watchTitle = { armed: 'armed', off: 'not armed', offline: 'watcher offline', fallback: 'schedule only', snoozed: 'snoozed' };
  lanes.push({ key: 'watch', label: 'Watch',
               blocks: (timeline?.watch || []).map((s) => {
                 const kind = watchKind[s.state] || 'off';
                 return span(s.start, s.end, kind, `${watchTitle[kind]} ${shopClock(s.start)}–${shopClock(s.end)}`, watchTitle[kind]);
               }).filter(Boolean),
               ticks: [] });
  const live = new Map((status?.armed?.present || []).map((p) => [`${p.who}|${p.source}`, p]));
  for (const l of timeline?.presence || []) {
    const blocks = l.spans.map((s) => span(s.start, s.end, 'present', `${l.who} (${SOURCE_LABEL[l.source] || l.source}) ${shopClock(s.start)}–${shopClock(s.end)}`)).filter(Boolean);
    const p = live.get(`${l.who}|${l.source}`);
    if (p && p.counts_until && new Date(p.counts_until) > now) {
      const g = span(now, p.counts_until, 'ghost', `keeps the watch off until ${shopClock(p.counts_until)} if nothing changes`);
      if (g) blocks.push(g);
    }
    lanes.push({ key: `p:${l.who}|${l.source}`, label: `${firstName(l.who)} · ${SOURCE_LABEL[l.source] || l.source}`, blocks, ticks: [] });
  }
  const controls = (timeline?.snoozes || []).map((z) => span(z.started_at, z.end || z.until, 'snoozed',
    `${z.scope === 'whole' ? 'whole shop' : z.camera_key} snoozed by ${z.by || '?'}${extendedNote(z)} ${shopClock(z.started_at)}–${shopClock(z.end || z.until)}`)).filter(Boolean);
  // The I'm here button's own events (source 'button'; a Shortcut run by hand stores 'manual').
  const manual = (timeline?.presence_events || []).filter((e) => e.source === 'button')
    .map((e) => ({ left: pct(new Date(e.at).getTime(), w), kind: e.event === 'arrive' ? 'present' : 'off',
                   title: `${e.who} pressed ${e.event === 'arrive' ? "I'm here" : "I'm leaving"} at ${shopClock(e.at)}` }));
  lanes.push({ key: 'controls', label: "Snooze · I'm here", blocks: controls, ticks: manual });
  lanes.push({ key: 'alerts', label: 'Alerts', blocks: [],
               ticks: (timeline?.incidents || []).map((a) => ({ left: pct(new Date(a.seen_at).getTime(), w), kind: a.outcome,
                                                                 title: `${alertTitle(a)}: ${a.outcome}` })) });
  return lanes;
}

export const STRIP_AXIS = ['6p', '9p', '12a', '3a', '6a'];

/** The day bar's "Night watch" lane for Central day `ymd` (DayBar blocks are Central minutes). */
export function nightWatchLane(timeline, ymd, cameraKey, colors) {
  const blocks = [];
  for (const s of timeline?.watch || []) {
    if (s.state === 'disarmed') continue;
    const start = minuteOnDay(s.start, ymd);
    const end = minuteOnDay(s.end, ymd);
    if (end > start) {
      blocks.push({ start, end, color: colors[s.state] || colors.armed,
                    label: s.state === 'armed' ? 'armed' : s.state === 'offline' ? 'offline' : '',
                    title: `${s.state} ${shopClock(s.start)}–${shopClock(s.end)}` });
    }
  }
  for (const a of timeline?.incidents || []) {
    if (cameraKey && a.camera_key !== cameraKey) continue;
    const m = minuteOnDay(a.seen_at, ymd);
    blocks.push({ start: Math.max(0, m - 1), end: Math.min(1440, m + 1),
                  color: ['held', 'let_go', 'dropped'].includes(a.outcome) ? colors.markHeld : colors.mark,
                  label: '', title: `${alertTitle(a)}: ${a.outcome}` });
  }
  return blocks;
}

/** The day's window for the timeline read: Central midnight to the next. */
export function dayWindow(ymd) {
  return { since: centralDate(ymd, 0).toISOString(), until: centralDate(shiftDate(ymd, 1), 0).toISOString() };
}

// ── the two buttons ──────────────────────────────────────────────────────────────────────────────

export const SNOOZE_MINUTES = [15, 30, 60, 120];

async function press(ui, message, path, body) {
  const pin = await ui.pin(message, { autofill: false });
  if (!pin) return { cancelled: true };
  const r = await securityFetch(path, { method: 'POST', body: JSON.stringify({ ...body, pin }) });
  if (!r.ok) {
    await ui.alert(r.message || 'JobQ refused that.');
    return { error: r.message, status: r.status };
  }
  return { ok: true, body: r.body };
}

/** I'm here (event 'arrive') / I'm leaving ('leave'): the typed PIN's Admin counts as on site. */
export function pressImHere({ ui, event = 'arrive' }) {
  return press(ui, event === 'arrive'
    ? "I'm here: enter YOUR Admin PIN (your name goes on the check-in):"
    : "I'm leaving: enter your Admin PIN:", '/security/presence/manual', { event });
}

/** Snooze the whole shop or one camera (the same target again = extended). */
export function pressSnooze({ ui, scope = 'whole', cameraKey = null, minutes = 30, reason = '' }) {
  const what = scope === 'whole' ? 'the whole shop' : cameraKey;
  return press(ui, `Snooze ${what} for ${minutes} min: enter YOUR Admin PIN:`, '/security/snooze',
    { scope, camera_key: scope === 'camera' ? cameraKey : null, minutes, reason: reason || null });
}

/** Arm now: end a snooze before it runs out. */
export function pressEndSnooze({ ui, snoozeId }) {
  return press(ui, 'Arm now (end the snooze): enter YOUR Admin PIN:', `/security/snooze/${snoozeId}/end`, {});
}
